import { create } from 'zustand'
import { temporal } from 'zundo'
import { useCallback, useRef, useState } from 'react'
import { useStoreApi } from '@xyflow/react'
import { debounce } from 'lodash'
import { logger } from 'v2/utils/logger'
import type { GenericEdge, GenericNode } from '../types'
import { useFlowRunStore, useFlowStore } from '../store'

// Create a logger instance for flow history

export enum FlowHistoryEvent {
  NodeAdd = 'NodeAdd',
  NodeDelete = 'NodeDelete',
  NodeDragStop = 'NodeDragStop',
  NodeChange = 'NodeChange',
  NodeConnect = 'NodeConnect',
  EdgeDelete = 'EdgeDelete',
  EdgeAdd = 'EdgeAdd',
  NodeDataChange = 'NodeDataChange',
}

interface FlowHistoryState {
  nodes: GenericNode[]
  edges: GenericEdge[]
  workflowHistoryEvent: FlowHistoryEvent | undefined
}

export const useFlowHistory = create<FlowHistoryState>()(
  temporal(
    () => ({
      nodes: [] as GenericNode[],
      edges: [] as GenericEdge[],
      workflowHistoryEvent: undefined as FlowHistoryEvent | undefined,
    }),
    {
      partialize: (state) => ({
        nodes: state.nodes,
        edges: state.edges,
        workflowHistoryEvent: state.workflowHistoryEvent,
      }),
      limit: 100,
    },
  ),
)

export function useFlowHistoryManager() {
  const store = useStoreApi()
  const [undoCallbacks, setUndoCallbacks] = useState<(() => void)[]>([])
  const [redoCallbacks, setRedoCallbacks] = useState<(() => void)[]>([])

  const onUndo = useCallback((callback: () => void) => {
    logger.debug('Registering undo callback')
    setUndoCallbacks((prev) => [...prev, callback])
    return () => {
      logger.debug('Removing undo callback')
      setUndoCallbacks((prev) => prev.filter((cb) => cb !== callback))
    }
  }, [])

  const onRedo = useCallback((callback: () => void) => {
    logger.debug('Registering redo callback')
    setRedoCallbacks((prev) => [...prev, callback])
    return () => {
      logger.debug('Removing redo callback')
      setRedoCallbacks((prev) => prev.filter((cb) => cb !== callback))
    }
  }, [])

  const undo = useCallback(() => {
    logger.debug('Executing undo operation')
    useFlowHistory.temporal.getState().undo()
    useFlowStore.getState().unselectNode()
    undoCallbacks.forEach((callback) => callback())
  }, [undoCallbacks])

  const redo = useCallback(() => {
    logger.debug('Executing redo operation')
    useFlowHistory.temporal.getState().redo()
    redoCallbacks.forEach((callback) => callback())
  }, [redoCallbacks])

  const saveStateToHistoryRef = useRef(
    debounce((event: FlowHistoryEvent) => {
      const { isFlowRunning } = useFlowRunStore.getState()
      // When flow is running, don't update the history
      if (isFlowRunning) return
      const { nodes, edges } = store.getState()
      logger.debug(`Saving state to history for event: ${event}`, {
        nodesCount: nodes.length,
        edgesCount: edges.length,
      })
      useFlowHistory.setState({
        workflowHistoryEvent: event,
        nodes: nodes as GenericNode[],
        edges: edges as GenericEdge[],
      })

      // Unsaved changes are now automatically detected by comparing flow state
    }, 500),
  )

  const saveStateToHistory = useCallback((event: FlowHistoryEvent) => {
    switch (event) {
      case FlowHistoryEvent.NodeAdd:
      case FlowHistoryEvent.NodeDelete:
      case FlowHistoryEvent.NodeDragStop:
      case FlowHistoryEvent.NodeChange:
      case FlowHistoryEvent.NodeConnect:
      case FlowHistoryEvent.EdgeDelete:
      case FlowHistoryEvent.EdgeAdd:
      case FlowHistoryEvent.NodeDataChange:
        saveStateToHistoryRef.current(event)
        break
      default:
        logger.warn(`Unhandled flow history event: ${event}`)
        break
    }
  }, [])

  return {
    saveStateToHistory,
    undo,
    redo,
    onUndo,
    onRedo,
  }
}
